import { createContext, useState, ReactNode, useEffect } from "react";

export type PrimaryCancellationReasonValue =
  | "price_too_high"
  | "desired_integrations_unavailable"
  | "missing_features"
  | "not_using_anymore";

export type PrimaryCancellationReason = {
  value: PrimaryCancellationReasonValue;
  label: string;
} | null;

export type PriceFeedback =
  | "might_consider_custom_plan"
  | "want_to_cancel"
  | null;

export type InnerOption = {
  label: string;
  value: string;
};

export interface CancelSubscriptionPayload {
  reason: Exclude<PrimaryCancellationReason, null>;
  reason_details: {
    price_feedback?: Exclude<PriceFeedback, null>;
    desired_integrations?: InnerOption[];
    missing_features?: InnerOption[];
    not_using_reasons?: InnerOption[];
  };
}

interface FeedbackContextType {
  primaryReason: PrimaryCancellationReason;
  priceFeedback: PriceFeedback;
  desiredIntegrations: InnerOption[];
  missingFeatures: InnerOption[];
  notUsingReasons: InnerOption[];
  otherCustomValue: string;
  toolSwitchedTo: string;
  isPrimaryButtonDisabled: boolean;
  setPrimaryReason: (reason: PrimaryCancellationReason) => void;
  togglePriceFeedback: (type: PriceFeedback) => void;
  toggleIntegration: (integration: InnerOption) => void;
  toggleOtherIntegration: () => void;
  toggleFeature: (feature: InnerOption) => void;
  toggleOtherFeature: () => void;
  toggleNotUsingReason: (reason: InnerOption) => void;
  toggleOtherNotUsingReason: () => void;
  buildPayload: () => CancelSubscriptionPayload | null;
  resetForm: () => void;
  setOtherCustomValue: (value: string) => void;
  setToolSwitchedTo: (value: string) => void;
}

export const FeedbackContext = createContext<FeedbackContextType | null>(null);

interface FeedbackProviderProps {
  children: ReactNode;
}

export const FeedbackProvider = ({ children }: FeedbackProviderProps) => {
  const [primaryReason, setPrimaryReasonState] =
    useState<PrimaryCancellationReason>(null);
  const [priceFeedback, setPriceFeedback] = useState<PriceFeedback>(null);
  const [desiredIntegrations, setDesiredIntegrations] = useState<InnerOption[]>(
    [],
  );
  const [missingFeatures, setMissingFeatures] = useState<InnerOption[]>([]);
  const [notUsingReasons, setNotUsingReasons] = useState<InnerOption[]>([]);
  const [otherCustomValue, setOtherCustomValue] = useState<string>("");
  const [toolSwitchedTo, setToolSwitchedTo] = useState<string>("");
  const [isPrimaryButtonDisabled, setIsPrimaryButtonDisabled] =
    useState<boolean>(true);

  const setPrimaryReason = (reason: PrimaryCancellationReason) => {
    if (reason?.value && reason?.value !== primaryReason?.value) {
      // Clear all inner states when switching primary reason
      setPriceFeedback(null);
      setDesiredIntegrations([]);
      setMissingFeatures([]);
      setNotUsingReasons([]);
      setOtherCustomValue("");
      setToolSwitchedTo("");
      setIsPrimaryButtonDisabled(true);

      setPrimaryReasonState(reason);
    }
  };

  const togglePriceFeedback = (type: PriceFeedback) => {
    setPriceFeedback((prev) => (prev === type ? null : type));
  };

  const toggleIntegration = (integration: InnerOption) => {
    setDesiredIntegrations((prev) => {
      return prev.some((item) => item.value === integration.value)
        ? prev.filter((item) => item.value !== integration.value)
        : [...prev, integration];
    });
  };

  const toggleOtherIntegration = () => {
    setDesiredIntegrations((prev) => {
      if (prev.some((item) => item.value.startsWith("other_"))) {
        setOtherCustomValue("");
        return prev.filter((item) => !item.value.startsWith("other_"));
      }
      return [...prev, { label: "Other", value: `other_${otherCustomValue}` }];
    });
    setOtherCustomValue("");
  };

  const toggleFeature = (feature: InnerOption) => {
    setMissingFeatures((prev) =>
      prev.some((item) => item.value === feature.value)
        ? prev.filter((item) => item.value !== feature.value)
        : [...prev, feature],
    );
  };

  const toggleOtherFeature = () => {
    setMissingFeatures((prev) => {
      if (prev.some((item) => item.value.startsWith("other_"))) {
        setOtherCustomValue("");
        return prev.filter((item) => !item.value.startsWith("other_"));
      }
      return [...prev, { label: "Other", value: `other_${otherCustomValue}` }];
    });
    setOtherCustomValue("");
  };

  const toggleNotUsingReason = (reason: InnerOption) => {
    setNotUsingReasons((prev) => {
      if (prev.some((item) => item.value === reason.value)) {
        return [];
      } else {
        return [reason];
      }
    });
    setToolSwitchedTo("");
  };

  const toggleOtherNotUsingReason = () => {
    setNotUsingReasons((prev) => {
      if (prev.some((item) => item.value.startsWith("other_"))) {
        setOtherCustomValue("");
        return [];
      }
      return [{ label: "Other", value: `other_${otherCustomValue}` }];
    });
    setToolSwitchedTo("");
    setOtherCustomValue("");
  };

  const buildPayload = (): CancelSubscriptionPayload | null => {
    if (!primaryReason) return null;

    const payload: CancelSubscriptionPayload = {
      reason: primaryReason,
      reason_details: {},
    };

    const updateToolSwitchedToIfPresent = (arr: InnerOption[]) => {
      const toolSwitchedToArrItem = arr.find((item) =>
        item.value.startsWith("switched-to-another-tool"),
      );
      if (toolSwitchedToArrItem) {
        toolSwitchedToArrItem.value = `switched-to-another-tool_${toolSwitchedTo}`;
      }
    };

    const updateOtherValueIfPresent = (arr: InnerOption[]) => {
      const otherIntegration = arr.find((item) =>
        item.value.startsWith("other_"),
      );
      if (otherIntegration) {
        otherIntegration.value = `other_${otherCustomValue}`;
      }
    };

    switch (primaryReason.value) {
      case "price_too_high":
        if (priceFeedback) {
          payload.reason_details.price_feedback = priceFeedback;
        }
        break;
      case "desired_integrations_unavailable":
        if (desiredIntegrations.length > 0) {
          payload.reason_details.desired_integrations = desiredIntegrations;
          updateOtherValueIfPresent(desiredIntegrations);
        }
        break;
      case "missing_features":
        if (missingFeatures.length > 0) {
          payload.reason_details.missing_features = missingFeatures;
          updateOtherValueIfPresent(missingFeatures);
        }
        break;
      case "not_using_anymore":
        if (notUsingReasons.length > 0) {
          payload.reason_details.not_using_reasons = notUsingReasons;
          updateOtherValueIfPresent(notUsingReasons);
          updateToolSwitchedToIfPresent(notUsingReasons);
        }
        break;
    }

    return payload;
  };

  const resetForm = () => {
    setPrimaryReasonState(null);
    setPriceFeedback(null);
    setDesiredIntegrations([]);
    setMissingFeatures([]);
    setNotUsingReasons([]);
    setIsPrimaryButtonDisabled(true);
    setOtherCustomValue("");
    setToolSwitchedTo("");
  };

  const checkIfPrimaryButtonDisabledForArray = (arr: InnerOption[]) => {
    if (arr?.length > 0) {
      if (arr.some((item) => item.value.startsWith("other_"))) {
        setIsPrimaryButtonDisabled(otherCustomValue === "");
      } else {
        setIsPrimaryButtonDisabled(false);
      }
    } else {
      setIsPrimaryButtonDisabled(true);
    }
  };

  useEffect(() => {
    switch (primaryReason?.value) {
      case "price_too_high":
        setIsPrimaryButtonDisabled(!priceFeedback);
        break;
      case "desired_integrations_unavailable":
        checkIfPrimaryButtonDisabledForArray(desiredIntegrations);
        break;
      case "missing_features":
        checkIfPrimaryButtonDisabledForArray(missingFeatures);
        break;
      case "not_using_anymore":
        checkIfPrimaryButtonDisabledForArray(notUsingReasons);
        break;
    }
  }, [
    primaryReason,
    priceFeedback,
    desiredIntegrations,
    missingFeatures,
    notUsingReasons,
    otherCustomValue,
  ]);

  const value: FeedbackContextType = {
    primaryReason,
    priceFeedback,
    desiredIntegrations,
    missingFeatures,
    notUsingReasons,
    otherCustomValue,
    toolSwitchedTo,
    isPrimaryButtonDisabled,
    setPrimaryReason,
    togglePriceFeedback,
    toggleIntegration,
    toggleOtherIntegration,
    toggleFeature,
    toggleOtherFeature,
    toggleNotUsingReason,
    toggleOtherNotUsingReason,
    buildPayload,
    resetForm,
    setOtherCustomValue,
    setToolSwitchedTo,
  };

  return (
    <FeedbackContext.Provider value={value}>
      {children}
    </FeedbackContext.Provider>
  );
};
